import { useState } from 'react';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { Title } from '../../../../shared/ModalComponents';
import useDropdown from '../../../../lib/hooks/useDropdown';
import { BaseInput, Label } from '../../../../shared/FormStyles';
import { Button } from '../../../../shared/FormStyles/Button';
import { createTemplate, editTemplate } from '../../data';
import Modal from '../../../../shared/Modal';
import { useNotification } from '../../../../shared/Notification/useNotification';
import defaultAttrs from '../../../../lib/utils/defaultAttrs';

const DropdownIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-solid fa-chevron-down',
  }),
)`
  margin: 0 0 0 8px;
  color: ${themeGet('colors.primary.100')};
  transition: all 0.7s ease-out;

  ${props => props.showingdropdown === 'true' && 'transform: rotateX(180deg);'}
`;

const List = styled.ul`
  position: absolute;
  top: 51px;
  right: 0;
  border-radius: 8px;
  background: white;
  z-index: 1;
  width: 400px;
  font-weight: 500;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  max-height: 80vh;
  overflow-y: auto;
`;

const Li = styled.li`
  cursor: pointer;
  width: 100%;

  &:hover {
    background: rgba(229, 242, 255, 0.5);
  }
`;

const Template = styled(Li)`
  padding: 11px 20px;

  &:nth-child(2) {
    padding: 16px 20px 11px;
  }

  &:last-child {
    padding: 11px 20px;
  }
`;

const FormWrapper = styled.div`
  display: ${props => (props.hide ? 'none' : 'block')};
`;

const CreateTemplate = styled(Li)`
  color: ${themeGet('colors.primary.100')};
  border-bottom: 1px solid ${themeGet('colors.black.20')};
  padding: 19px 20px 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 0 0;
`;

const Form = ({ mutate, onCancel }) => {
  const [templateName, setTemplateName] = useState('');
  const { t } = useTranslation(['clients', 'shared']);

  return (
    <FormWrapper>
      <Title>{t('clients:form.createTemplate')}</Title>
      <Label>{t('clients:form.createTemplateLabel')}</Label>
      <BaseInput
        value={templateName}
        onChange={e => setTemplateName(e.target.value)}
        type="text"
        placeholder={t('clients:form.name')}
      />
      <Actions>
        <Button variant="tertiary" type="button" onClick={onCancel}>
          {t('shared:cancel')}
        </Button>
        <Button
          variant="containedBlue"
          ml="16px"
          type="button"
          disabled={templateName.length === 0}
          onClick={() => mutate({ templateName })}
        >
          {t('shared:create')}
        </Button>
      </Actions>
    </FormWrapper>
  );
};

const EditTemplate = ({ templateList }) => {
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();
  const { t } = useTranslation(['clients', 'shared']);
  const { showError } = useNotification();

  const mutationSideEffects = {
    onSuccess: ({ data }) => {
      window.location.assign(data.path);
    },
    onError: () => {
      showError({ content: t('shared:errorText') });
    },
  };

  const { mutate: createTemplateMutation } = useMutation(
    createTemplate,
    mutationSideEffects,
  );
  const { mutate: editTemplateMutation } = useMutation(
    editTemplate,
    mutationSideEffects,
  );

  const openModal = () => {
    Modal({
      component: (
        <Form onCancel={Swal.clickCancel} mutate={createTemplateMutation} />
      ),
      padding: 24,
    });
  };

  return (
    <div style={{ marginRight: 16, position: 'relative' }}>
      <Button
        variant="outlinedBlue"
        padding="11px 16px"
        ref={buttonRef}
        onClick={() => setShowDropdown(state => !state)}
      >
        {t('clients:form.editTemplate')}
        <DropdownIcon showingdropdown={showDropdown.toString()} />
      </Button>
      {showDropdown && (
        <List ref={dropdownRef}>
          <CreateTemplate onClick={openModal}>
            {t('clients:form.createTemplate')}
          </CreateTemplate>
          {templateList.map(template => (
            <Template
              key={template.id}
              onClick={() => editTemplateMutation({ templateId: template.id })}
            >
              {template.name}
            </Template>
          ))}
        </List>
      )}
    </div>
  );
};

export default EditTemplate;
